// Shared visual effects — currently just the death splatter. Every cat
// death in the game (peasant, scribe, noble, wild goon, hero shot) routes
// through `splatterBurst` so the player gets a single recognizable
// "splatter" cue rather than the older mix of tip-over / shrink animations.

import * as THREE from 'three'
import gsap from 'gsap'
import { addGlow, addNightObject } from './nightLights.js'

const FRAG_GEOM = new THREE.TetrahedronGeometry(0.025) // small splatter chips

// Low-poly particle burst at `position` using a palette of `colors`. Shards
// fly outward + upward, spin randomly, shrink to nothing, then dispose
// their geometry/material to avoid leaks. Geometry is shared across all
// shards (frozen tetrahedron) for cheap GC.
export function splatterBurst({ scene, position, colors, count = 16 }) {
  if (!scene || !position || !colors?.length) return
  const origin = position.clone()
  origin.y += 0.18 // burst from the body center, not the floor

  for (let i = 0; i < count; i++) {
    const color = colors[i % colors.length]
    const mat = new THREE.MeshLambertMaterial({ color })
    const frag = new THREE.Mesh(FRAG_GEOM, mat)
    frag.position.copy(origin)
    scene.add(frag)

    const theta = Math.random() * Math.PI * 2
    const phi = Math.random() * Math.PI * 0.5 // bias upward
    const speed = 0.35 + Math.random() * 0.45
    const tx = origin.x + Math.cos(theta) * Math.cos(phi) * speed
    const ty = origin.y + Math.sin(phi) * speed * 0.5 + 0.08
    const tz = origin.z + Math.sin(theta) * Math.cos(phi) * speed

    gsap.to(frag.position, { x: tx, y: ty, z: tz, duration: 0.6, ease: 'power2.out' })
    gsap.to(frag.rotation, {
      x: Math.random() * Math.PI * 4,
      y: Math.random() * Math.PI * 4,
      z: Math.random() * Math.PI * 4,
      duration: 0.6,
    })
    gsap.to(frag.scale, {
      x: 0,
      y: 0,
      z: 0,
      duration: 0.4,
      delay: 0.2,
      ease: 'power1.in',
      onComplete: () => {
        scene.remove(frag)
        // Geometry is shared — only dispose the material.
        frag.material.dispose()
      },
    })
  }
}

// ── FIRE EMITTER ────────────────────────────────────────────────────────
// Continuous low-poly flame for burning buildings. Spawns a cluster of
// cone "tongues" that rise, taper, and fade — each on its own looping
// timeline so the whole thing flickers organically — plus a few ember
// sparks that pop higher. Returns a handle with dispose() to tear it all
// down (kills tweens, removes meshes, frees materials). Shared geometries
// keep allocation cheap even with many simultaneous fires.
const FLAME_GEOM = new THREE.ConeGeometry(0.055, 0.18, 5)
const EMBER_GEOM = new THREE.TetrahedronGeometry(0.018)
// Voxel smoke = small cubes. Shared geometry; each puff gets its own material
// so opacity can fade independently per particle without leaks.
const SMOKE_GEOM = new THREE.BoxGeometry(0.06, 0.06, 0.06)
const FLAME_COLORS = [0xff3008, 0xff5a14, 0xff8c1a, 0xffb733, 0xffd84d]
// Greys ranging from dark soot to lighter wisp — randomized per puff.
const SMOKE_COLORS = [0x222222, 0x383838, 0x4a4a4a, 0x5e5e5e, 0x747474]

export function createFire({ scene, position, scale = 1 }) {
  if (!scene || !position) return { dispose() {} }
  const group = new THREE.Group()
  group.position.copy(position)
  group.scale.setScalar(scale)
  scene.add(group)

  const parts = []
  const FLAME_COUNT = 9
  const EMBER_COUNT = 4

  for (let i = 0; i < FLAME_COUNT; i++) {
    const mat = new THREE.MeshBasicMaterial({
      color: FLAME_COLORS[i % FLAME_COLORS.length],
      transparent: true,
      opacity: 0.92,
      depthWrite: false, // flames blend over each other instead of z-fighting
    })
    const flame = new THREE.Mesh(FLAME_GEOM, mat)
    group.add(flame)
    parts.push(flame)
    loopFlame(flame)
  }

  for (let i = 0; i < EMBER_COUNT; i++) {
    const mat = new THREE.MeshBasicMaterial({
      color: 0xffd06a,
      transparent: true,
      opacity: 1,
      depthWrite: false,
    })
    const ember = new THREE.Mesh(EMBER_GEOM, mat)
    group.add(ember)
    parts.push(ember)
    loopEmber(ember)
  }

  // Voxel smoke puffs rise above the flames, drift sideways, grow as they
  // ascend (hot air expands), then fade. Staggered start delay so they
  // chain into a continuous column instead of pulsing in sync.
  const SMOKE_COUNT = 7
  for (let i = 0; i < SMOKE_COUNT; i++) {
    const mat = new THREE.MeshBasicMaterial({
      color: SMOKE_COLORS[i % SMOKE_COLORS.length],
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
    })
    const smoke = new THREE.Mesh(SMOKE_GEOM, mat)
    group.add(smoke)
    parts.push(smoke)
    // Stagger initial launch so puffs aren't all at the base at t=0.
    setTimeout(() => loopSmoke(smoke), (i / SMOKE_COUNT) * 900)
  }

  function loopFlame(p) {
    const baseX = (Math.random() - 0.5) * 0.22
    const baseZ = (Math.random() - 0.5) * 0.22
    const rise = 0.3 + Math.random() * 0.25
    const dur = 0.45 + Math.random() * 0.45
    p.position.set(baseX, 0, baseZ)
    p.rotation.y = Math.random() * Math.PI
    p.scale.setScalar(0.7 + Math.random() * 0.7)
    p.material.opacity = 0.92
    gsap.killTweensOf(p.position)
    gsap.killTweensOf(p.scale)
    gsap.killTweensOf(p.material)
    gsap.to(p.position, { y: rise, x: baseX * 0.3, z: baseZ * 0.3, duration: dur, ease: 'power1.out' })
    gsap.to(p.scale, { x: 0.12, y: 0.35, z: 0.12, duration: dur, ease: 'power2.in' })
    gsap.to(p.material, {
      opacity: 0,
      duration: dur * 0.7,
      delay: dur * 0.3,
      ease: 'power1.in',
      onComplete: () => loopFlame(p),
    })
  }

  function loopSmoke(p) {
    if (!p.parent) return // disposed mid-stagger
    const baseX = (Math.random() - 0.5) * 0.12
    const baseZ = (Math.random() - 0.5) * 0.12
    const rise = 0.9 + Math.random() * 0.7              // climbs well above flames
    const driftX = (Math.random() - 0.5) * 0.6           // wind drift
    const driftZ = (Math.random() - 0.5) * 0.6
    const startS = 0.5 + Math.random() * 0.4             // small near the fire
    const endS = 2.0 + Math.random() * 0.8               // expands while rising
    const dur = 1.4 + Math.random() * 0.8                // slower than flames
    p.position.set(baseX, 0.25, baseZ)
    p.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI)
    p.scale.setScalar(startS)
    p.material.opacity = 0.55
    gsap.killTweensOf(p.position)
    gsap.killTweensOf(p.scale)
    gsap.killTweensOf(p.rotation)
    gsap.killTweensOf(p.material)
    gsap.to(p.position, {
      y: rise, x: baseX + driftX, z: baseZ + driftZ,
      duration: dur, ease: 'power1.out',
    })
    gsap.to(p.scale, { x: endS, y: endS, z: endS, duration: dur, ease: 'power2.out' })
    gsap.to(p.rotation, {
      x: p.rotation.x + (Math.random() - 0.5) * 1.2,
      z: p.rotation.z + (Math.random() - 0.5) * 1.2,
      duration: dur,
      ease: 'none',
    })
    gsap.to(p.material, {
      opacity: 0,
      duration: dur * 0.6,
      delay: dur * 0.4,
      ease: 'power1.in',
      onComplete: () => loopSmoke(p),
    })
  }

  function loopEmber(p) {
    const baseX = (Math.random() - 0.5) * 0.18
    const baseZ = (Math.random() - 0.5) * 0.18
    const rise = 0.55 + Math.random() * 0.4
    const drift = (Math.random() - 0.5) * 0.25
    const dur = 0.7 + Math.random() * 0.6
    p.position.set(baseX, 0.1, baseZ)
    p.scale.setScalar(0.6 + Math.random() * 0.5)
    p.material.opacity = 1
    gsap.killTweensOf(p.position)
    gsap.killTweensOf(p.material)
    gsap.to(p.position, { y: rise, x: baseX + drift, z: baseZ + drift, duration: dur, ease: 'power1.out' })
    gsap.to(p.material, {
      opacity: 0,
      duration: dur,
      ease: 'power1.in',
      onComplete: () => loopEmber(p),
    })
  }

  return {
    group, // exposed so callers (e.g. campfires) can night-gate visibility
    dispose() {
      for (const p of parts) {
        gsap.killTweensOf(p.position)
        gsap.killTweensOf(p.scale)
        gsap.killTweensOf(p.rotation)
        gsap.killTweensOf(p.material)
        p.material.dispose() // geometries are shared — don't dispose
      }
      scene.remove(group)
    },
  }
}

// ── CAMPFIRE ────────────────────────────────────────────────────────────
// A cozy village campfire: a small crossed log pile + the low-poly flame
// emitter above + a warm light/ground-pool that lights up at night (via
// nightLights). Self-animating (the flame loops on its own). dispose() tears
// down the flame + removes the logs.
const LOG_GEOM = new THREE.BoxGeometry(0.34, 0.08, 0.08)
const LOG_MAT = new THREE.MeshLambertMaterial({ color: 0x5b3d27 })

// Campfires on the field (raiders stop to light their torches at a burning one — see wildGoons.js).
const campfires = []
export const getCampfires = () => campfires.filter((c) => c.obj.parent)

export function createCampfire({ scene, position, scale = 0.85 }) {
  if (!scene || !position) return { dispose() {} }
  const logs = new THREE.Group()
  campfires.push({ obj: logs, x: position.x, z: position.z })
  logs.position.copy(position)
  logs.scale.setScalar(scale)
  scene.add(logs)
  for (let i = 0; i < 4; i++) {
    const log = new THREE.Mesh(LOG_GEOM, LOG_MAT)
    log.position.y = 0.04
    log.rotation.y = (i / 4) * Math.PI + 0.3
    log.rotation.z = (Math.random() - 0.5) * 0.22
    logs.add(log)
  }
  const fire = createFire({ scene, position, scale: scale * 0.95 })
  // The whole campfire (logs + flame) only appears at night — hidden by day and
  // whenever night mode is off.
  addNightObject(logs)
  addNightObject(fire.group)
  // Warm point glow at the flame + a broad pool on the ground — night only.
  addGlow(position.x, position.y + 0.3 * scale, position.z,
    { size: 0.65, intensity: 0.55, color: '#ff7a2a', flicker: 0.28 })
  addGlow(position.x, position.y + 0.04, position.z,
    { size: 0.9, intensity: 0.26, color: '#ff8a36', flicker: 0.18, flat: true })

  return {
    dispose() {
      fire.dispose()
      scene.remove(logs)
    },
  }
}

// Golden starburst — where the hero (re)appears, so the player can't miss the cat: a flash of gold shards,
// two expanding shock rings and a quick vertical light beam.
export function goldenStarburst({ scene, position }) {
  if (!scene || !position) { if (import.meta.env?.DEV) console.warn('[starburst] no scene/position', !!scene, !!position); return }
  if (import.meta.env?.DEV) window.__bursts = (window.__bursts || 0) + 1
  splatterBurst({ scene, position, colors: [0xffd84d, 0xfff3c4, 0xffb733, 0xffffff], count: 30 })
  const base = position.clone()
  const mk = (geo, color, opacity) => {
    const m = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide }))
    m.position.copy(base); scene.add(m); return m
  }
  const done = (m) => { scene.remove(m); m.geometry.dispose(); m.material.dispose() }
  for (let i = 0; i < 2; i++) {
    const ring = mk(new THREE.RingGeometry(0.1, 0.14, 48), i ? 0xfff3c4 : 0xffd84d, 0.95)
    ring.rotation.x = -Math.PI / 2; ring.position.y += 0.04; ring.scale.setScalar(0.3)
    gsap.to(ring.scale, { x: 5.5, y: 5.5, z: 5.5, duration: 0.9, delay: i * 0.18, ease: 'power2.out' })
    gsap.to(ring.material, { opacity: 0, duration: 0.9, delay: i * 0.18, ease: 'power1.in', onComplete: () => done(ring) })
  }
  const beam = mk(new THREE.CylinderGeometry(0.1, 0.22, 1.8, 20, 1, true), 0xffe28a, 0.6)
  beam.position.y += 0.9
  gsap.to(beam.scale, { x: 0.2, z: 0.2, duration: 1.0, ease: 'power2.in' })
  gsap.to(beam.material, { opacity: 0, duration: 1.0, ease: 'power1.in', onComplete: () => done(beam) })
}
